// TODO notes (docs/BLIP_TODO_NOTES_IMPLEMENTATION_PLAN.md): a message to Blip
// that starts with TODO is a note for later, not a request to act on now.
// Blip gives the note's parts through save_todo (web/ide/blip/tools.json),
// and the page writes it here, in the project's todo folder, in a fixed
// shape: with the user's words as they typed them, the pictures shown with
// it at full size, and their paths, so that a coding agent can follow it.
// When Blip could not write it up, the page saves it as typed.
//
// A note is todo/<date>-<slug>.md, its pictures are
// todo/pictures/<date>-<slug>-<n>.png (or .jpg), and the folder's README.md
// is written once, when it has none.

import { pictureNumber } from "./guides.js";
import { freePath } from "./models.js";

export class TodoError extends Error {}

export const FOLDER = "todo";
// The most notes one request saves.
export const MOST_NOTES = 5;
// A full-size picture's long side, at most.
const LONGEST = 8192;
const TITLE = 80, SLUG = 48, TITLE_WORDS = 8;
const KINDS = ["problem", "improvement", "feature", "idea", "question"];
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October",
  "November", "December"];

// The line the context gets for a note's message (panel.js).
export const TODO_CONTEXT = "This message starts with TODO: it is a note for later, not a request to act on now. Write it " +
  "up with save_todo, as your notes on TODO notes say, and change nothing: the tools that change things refuse until the " +
  "user's next message. Its pictures go with the note.";

// The folder's README, for whoever is pointed at it.
export const README = `# TODO notes

Each note here is one task, written by Blip, the Blitz3D+ IDE's helper,
from a message that started with TODO. Its pictures are in pictures/.

To do one, read the whole note and look at its pictures first. "Done when"
lists the checks that show it is done. "As written" is the note exactly as
it was typed; when the rest of the note disagrees with it, it wins. When the
task is done, change the note's status to "done", and say what changed
under a heading "Outcome".

The web, Apple and Android packages that Blitz3D+ makes leave this folder
out. Leave it out yourself when you share the project another way.
`;

// Whether a message is a note: TODO, in capitals, at its start, then its
// end, a space or a mark, as in "TODO:" and "TODO - ".
export const isTodo = (text) => /^TODO(?=$|[\s:.,;!\-–—])/.test(String(text || "").trim());

// A note's words after its TODO.
export const todoWords = (text) => String(text || "").trim().replace(/^TODO[\s:.,;!\-–—]*/, "").trim();

const oneLine = (text) => String(text ?? "").replace(/\s+/g, " ").trim();
// Paragraphs, with \n line ends, no blank lines past one, and no spaces at
// the lines' ends.
const paragraphs = (text) => String(text ?? "").replace(/\r\n?/g, "\n").replace(/[ \t]+$/gm, "").replace(/\n{3,}/g, "\n\n").trim();
const pad = (number) => String(number).padStart(2, "0");
const plural = (count, one, many = `${one}s`) => `${count} ${count === 1 ? one : many}`;

// A date as the user's computer has it: 2026-10-21.
export const localDate = (date) => `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

// A time with its time zone: 2026-10-21T10:15:03+01:00.
export function isoTime(date) {
  const offset = -date.getTimezoneOffset();
  const zone = `${offset < 0 ? "-" : "+"}${pad(Math.floor(Math.abs(offset) / 60))}:${pad(Math.abs(offset) % 60)}`;
  return `${localDate(date)}T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}${zone}`;
}

// A title as a file's name: lower case, hyphens between its words, at most
// 48 characters, cut where a word ends.
export function noteSlug(title) {
  const whole = String(title || "").toLowerCase().normalize("NFKD").replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
  if (whole.length <= SLUG) return whole || "note";
  const cut = whole.slice(0, SLUG + 1);
  const end = cut.lastIndexOf("-");
  return (end > 0 ? cut.slice(0, end) : whole.slice(0, SLUG)).replace(/-+$/, "");
}

// A fence for a block of text: three backticks, or one more than the longest
// run in it, so that nothing in it ends the block.
export function fence(text) {
  const longest = Math.max(0, ...(String(text).match(/`+/g) || []).map((run) => run.length));
  return "`".repeat(Math.max(3, longest + 1));
}

// A project file's full path, from its folder's: with backslashes in a
// Windows folder.
export function fullPath(folder, path) {
  const windows = /^[a-z]:/i.test(folder) || folder.includes("\\");
  const separator = windows ? "\\" : "/";
  return `${folder.replace(/[\\/]+$/, "")}${separator}${path.split("/").join(separator)}`;
}

const link = (path) => path.split("/").map(encodeURIComponent).join("/");

// A note's Markdown. note: { title, kind, area, task, now, wanted, doneWhen,
// context, questions, pictures: [{ path (from the note), width, height,
// shows, full (its full path, or null), small (saved at the size Blip saw
// it) }], words (the message as typed), names (its pictures' file names),
// created (a Date), project, world, by, place (where it was noted: "in the
// desktop IDE on Windows"), asTyped (why Blip couldn't write it up, or
// null) }. Every value of the front matter is a JSON string, which is valid
// YAML too.
export function todoMarkdown(note) {
  const created = note.created || new Date();
  const out = ["---"];
  const meta = (key, value) => {
    if (value !== undefined && value !== null && value !== "") out.push(`${key}: ${JSON.stringify(String(value))}`);
  };
  meta("title", oneLine(note.title));
  meta("status", "open");
  meta("kind", note.kind);
  meta("area", oneLine(note.area));
  meta("created", isoTime(created));
  meta("project", note.project);
  meta("world", note.world);
  out.push(`pictures: ${JSON.stringify((note.pictures || []).map((picture) => picture.path))}`);
  meta("by", note.by);
  out.push("---", "", `# ${oneLine(note.title)}`);
  const part = (heading, body) => {
    if (body) out.push("", `## ${heading}`, "", body);
  };
  const items = (list, mark) => (list || []).map(oneLine).filter(Boolean).map((item) => `${mark}${item}`).join("\n");
  part("What to do", paragraphs(note.task));
  part("What happens now", paragraphs(note.now));
  part("What should happen", paragraphs(note.wanted));
  part("Done when", items(note.doneWhen, "- [ ] "));
  part("Context", items(note.context, "- "));
  part("Pictures", (note.pictures || []).map((picture, index) => {
    const lines = [`${index + 1}. [${picture.path}](${link(picture.path)}), ${picture.width} x ${picture.height}` +
      `${picture.small ? " (saved at the size Blip saw it)" : ""}: ${oneLine(picture.shows) || "(Shown with the note.)"}`];
    if (picture.full) lines.push(`   On this computer: \`${picture.full}\``);
    lines.push("", `   ![Picture ${index + 1}](${link(picture.path)})`);
    return lines.join("\n");
  }).join("\n\n"));
  part("Questions", items(note.questions, "- "));
  const words = String(note.words || "").replace(/\r\n?/g, "\n").replace(/\n+$/, "");
  const ticks = fence(words);
  const names = (note.names || []).filter(Boolean);
  part("As written", `${ticks}text\n${words}\n${ticks}` +
    (names.length ? `\n\nWith ${names.length === 1 ? "one picture" : plural(names.length, "picture")}: ${names.join(", ")}.` : ""));
  out.push("", "---", "", `${note.asTyped ? "Saved as typed" : "Noted with Blip"} on ${created.getDate()} ` +
    `${MONTHS[created.getMonth()]} ${created.getFullYear()} at ${pad(created.getHours())}:${pad(created.getMinutes())}` +
    `${note.place ? `, ${note.place}` : ""}.${note.asTyped ? ` Blip couldn't write it up: ${note.asTyped}.` : ""} When it is ` +
    "done, change its status to \"done\", and say what changed under a heading \"Outcome\".");
  return `${out.join("\n")}\n`;
}

// A picture the user showed, drawn again at its own size, the right way up,
// at most 8192 pixels on its long side: encoding it again leaves out the
// file's metadata, as guides.js does for the copy Blip sees. A PNG, but a
// JPEG file, a photo, stays a JPEG. Returns { bytes, type, width, height }.
export async function fullPicture(original) {
  const blob = original instanceof Blob ? original : new Blob([await original.arrayBuffer()]);
  const head = new Uint8Array(await blob.slice(0, 3).arrayBuffer());
  const jpeg = head[0] === 0xff && head[1] === 0xd8 && head[2] === 0xff;
  const image = await createImageBitmap(blob, { imageOrientation: "from-image" });
  try {
    const scale = Math.min(1, LONGEST / Math.max(image.width, image.height));
    const width = Math.max(1, Math.round(image.width * scale)), height = Math.max(1, Math.round(image.height * scale));
    const canvas = new OffscreenCanvas(width, height);
    const context = canvas.getContext("2d");
    context.imageSmoothingQuality = "high";
    context.drawImage(image, 0, 0, width, height);
    const encoded = await canvas.convertToBlob(jpeg ? { type: "image/jpeg", quality: 0.92 } : { type: "image/png" });
    return { bytes: new Uint8Array(await encoded.arrayBuffer()), type: encoded.type, width, height };
  } finally {
    image.close();
  }
}

const EXTENSIONS = { "image/png": ".png", "image/jpeg": ".jpg", "image/gif": ".gif", "image/webp": ".webp" };
const encode = (text) => new TextEncoder().encode(text);

export class TodoNotes {
  // ide: the panel's IDE (panel.js): { project(), addFile(path, bytes),
  // removeFile(path), editor, desktop, and on the desktop folder() (the
  // project's folder, a full path), todoFolder() (the folder chosen for
  // notes, which the IDE maps todo/ to, or null), platform() and
  // version() }. The panel sets panel: { picture(n) (the user's picture n in
  // the chat, guides.js chatPicture), pictureCount(), about() ({ by, world }) }.
  constructor(ide) {
    this.ide = ide;
    this.panel = null;
    // Notes are written one at a time, so that two never take one name.
    this.writing = Promise.resolve();
    this.begin();
  }

  // A new request. todo: whether its message is a note; words: the message
  // as typed; pictures: those shown with it, [{ n, original (the file shown,
  // or null), name }].
  begin({ todo = false, words = "", pictures = [] } = {}) {
    this.todo = todo;
    this.words = words;
    this.own = pictures;
    this.saved = [];          // the notes saved: [{ path, full, title, text, pictures: [{ n, path }] }]
    this.taken = new Set();   // the request's pictures a note has taken
  }

  folder() {
    return this.ide.folder ? this.ide.folder() || null : null;
  }

  todoFolder() {
    return this.ide.todoFolder ? this.ide.todoFolder() || null : null;
  }

  // A project file's full path, on the desktop: one under todo/ is in the
  // folder chosen for notes, when there is one. null online.
  full(path) {
    const todo = this.todoFolder();
    if (todo && path.toLowerCase().startsWith(`${FOLDER}/`)) return fullPath(todo, path.slice(FOLDER.length + 1));
    const folder = this.folder();
    return folder ? fullPath(folder, path) : null;
  }

  // Why no note can be saved now, or "".
  unsaveable() {
    if (!this.ide.project()) return "No project is open";
    if (this.ide.desktop && !this.folder() && !this.todoFolder()) return "No project is open in the IDE";
    return "";
  }

  // Where the note was noted, for its last line.
  place(world) {
    const project = this.ide.project();
    let where;
    if (this.ide.desktop) {
      const platform = this.ide.platform ? this.ide.platform() : null;
      const version = this.ide.version ? this.ide.version() : null;
      where = `in the desktop IDE${platform ? ` on ${platform}` : ""}${version ? `, ${version}` : ""}`;
    } else {
      where = `in the online IDE, ${project.isFolder ? `in the folder ${project.meta.folderName || project.name} on your ` +
        "computer" : `in its project ${project.name}`}`;
    }
    return `${where}${world ? `, with ${world} open in the World Editor` : ""}`;
  }

  // save_todo: a note from Blip's parts. Returns what to tell him.
  async save(input) {
    const problem = this.unsaveable();
    if (problem) {
      throw new TodoError(`${problem}, so there is no todo folder to save the note in. Tell the user to open a project ` +
        "first (File > Open Project...), then send the note again.");
    }
    if (this.saved.length >= MOST_NOTES) throw new TodoError(`A request saves at most ${MOST_NOTES} notes.`);
    const title = oneLine(input.title).slice(0, TITLE);
    if (!title) throw new TodoError("Give the note a title: what is to be done, in a few words.");
    if (!paragraphs(input.task)) throw new TodoError("Give the note its task: what to do, and why.");
    for (const key of ["done_when", "context", "questions"]) {
      if (input[key] !== undefined && (!Array.isArray(input[key]) || input[key].some((item) => typeof item !== "string"))) {
        throw new TodoError(`${key} is a list of short texts.`);
      }
    }
    if (input.pictures !== undefined && !Array.isArray(input.pictures)) {
      throw new TodoError("pictures is a list of { picture, shows }.");
    }
    const chosen = [];
    for (const entry of input.pictures || []) {
      const n = pictureNumber(entry && entry.picture);
      if (n === null) throw new TodoError("Each of pictures is { picture, shows }, picture being the number of one the user showed you.");
      if (!this.panel || !this.panel.picture(n)) {
        const count = this.panel ? this.panel.pictureCount() : 0;
        throw new TodoError(`There is no picture ${n} in this chat: the user has shown ${count ?
          `${count === 1 ? "one picture" : `${count} pictures`}, numbered from 1` : "no pictures"}.`);
      }
      if (!chosen.some((one) => one.n === n)) chosen.push({ n, shows: oneLine(entry.shows) });
    }
    // The message's own pictures go with its first note, named or not.
    for (const own of this.own) {
      if (!this.taken.has(own.n) && !chosen.some((one) => one.n === own.n)) chosen.push({ n: own.n, shows: "" });
    }
    const about = this.panel && this.panel.about ? this.panel.about() : {};
    const { record, pictures } = await this.write({ title, kind: KINDS.includes(input.kind) ? input.kind : undefined,
      area: input.area, task: input.task, now: input.now, wanted: input.wanted, doneWhen: input.done_when,
      context: input.context, questions: input.questions, chosen, by: about.by || "Blip" });
    const saved = pictures.map((picture) => `picture ${picture.n} as ${picture.saved} (${picture.width} x ${picture.height})`);
    return `Saved the TODO note ${record.path}${saved.length ? `, with ${saved.join(", ")}` : ""}. End your turn with one short ` +
      "sentence that says what you noted; don't repeat the note.";
  }

  // The note as typed, when Blip did not write it up: reason says why. Not
  // when a note was saved, or the message holds nothing to save. Returns
  // its record, or null.
  async saveAsTyped(reason) {
    if (this.saved.length || this.unsaveable()) return null;
    const words = todoWords(this.words);
    if (!words && !this.own.length) return null;
    const first = words.split(/\s+/).slice(0, TITLE_WORDS).join(" ").slice(0, TITLE).replace(/[\s:.,;!\-–—]+$/, "");
    const title = first ? `${first[0].toUpperCase()}${first.slice(1)}` : "A note with pictures";
    const { record } = await this.write({ title, task: words || "(The note is its pictures.)",
      chosen: this.own.map((own) => ({ n: own.n, shows: "" })), by: "the IDE, as typed", asTyped: reason });
    return record;
  }

  // Writes a note's pictures, then the note, then the folder's README when
  // it has none. Returns { record, pictures }.
  write({ title, chosen, by, asTyped = null, ...parts }) {
    const done = this.writing.then(async () => {
      const project = this.ide.project();
      const created = new Date();
      const about = this.panel && this.panel.about ? this.panel.about() : {};
      const path = freePath(project, `${FOLDER}/${localDate(created)}-${noteSlug(title)}.md`);
      const stem = path.slice(FOLDER.length + 1, -3);
      const pictures = [];
      for (const [index, entry] of chosen.entries()) {
        const picture = await this.picture(entry.n);
        const saved = freePath(project, `${FOLDER}/pictures/${stem}-${index + 1}${EXTENSIONS[picture.type] || ".png"}`);
        await this.ide.addFile(saved, picture.bytes);
        this.taken.add(entry.n);
        pictures.push({ n: entry.n, saved, path: saved.slice(FOLDER.length + 1), width: picture.width, height: picture.height,
          small: picture.small, shows: entry.shows, full: this.full(saved) });
      }
      // On the desktop with no program open, notes go to the chosen folder,
      // and the note names no project.
      const named = !this.ide.desktop || this.folder() ? project.name : null;
      const text = todoMarkdown({ ...parts, title, pictures, words: this.words, names: this.own.map((own) => own.name),
        created, project: named, world: about.world || null, by, place: this.place(about.world), asTyped });
      await this.ide.addFile(path, encode(text));
      if (!project.paths().some((other) => other.toLowerCase() === `${FOLDER}/readme.md`)) {
        await this.ide.addFile(`${FOLDER}/README.md`, encode(README));
      }
      const record = { path, full: this.full(path), title, text,
        pictures: pictures.map((picture) => ({ n: picture.n, path: picture.saved })) };
      this.saved.push(record);
      return { record, pictures };
    });
    this.writing = done.catch(() => {});
    return done;
  }

  // The user's picture n, at full size when the file shown is at hand, or
  // else as the chat keeps it: { bytes, type, width, height, small }.
  async picture(n) {
    const own = this.own.find((one) => one.n === n);
    if (own && own.original) {
      try {
        return { ...(await fullPicture(own.original)), small: false };
      } catch (error) {
        console.warn("The picture could not be saved at full size:", error);
      }
    }
    const shown = this.panel.picture(n);
    let { width, height } = shown;
    if (!width || !height) {
      const image = await createImageBitmap(new Blob([shown.bytes], { type: shown.type }));
      ({ width, height } = image);
      image.close();
    }
    return { bytes: shown.bytes, type: shown.type, width, height, small: true };
  }

  // Undo of a card's notes: each note, and its pictures, unless the note
  // has changed since. The note goes first, so that one the IDE won't
  // remove, as when it is open in a tab, keeps its pictures. Returns
  // { removed, kept }, their paths.
  async undo(records) {
    const project = this.ide.project();
    const removed = [], kept = [];
    for (const record of records) {
      if (!project || !project.has(record.path)) continue;
      const open = this.ide.editor && this.ide.editor.textOf ? this.ide.editor.textOf(record.path) : undefined;
      const now = (open !== undefined ? open : await project.text(record.path)).replace(/\r\n?/g, "\n");
      if (now !== record.text) {
        kept.push(record.path);
        continue;
      }
      await this.ide.removeFile(record.path);
      for (const picture of record.pictures) if (project.has(picture.path)) await this.ide.removeFile(picture.path);
      removed.push(record.path);
    }
    return { removed, kept };
  }
}
